<template>
  <div class="depositDiscount">
    <div class="selection_container">
      <p class="title font_size">{{ $t('depositDiscount.name') }}</p>
      <!-- Display if no voucher available -->
      <p class="notAvailable" v-if="noVoucherAvailable">
        {{ $t('depositDiscount.notAvailable') }}
        <router-link :to="{ name: 'clubBleu', query: { tab: 'claimRewards' } }"> Redeem Now </router-link>
      </p>
      <!-- Display select dropdown if voucher available -->
      <div
        v-else
        class="relative min-h-[80px] p-[20px] mt-[15px] bg-[#f2f6fc] rounded-[6px] flex items-center justify-start"
      >
        <el-select
          v-on="listeners"
          value=""
          class="absolute top-0 bottom-0 left-0 right-0"
          :class="{ isDisabled: disableDropdown || isDisable }"
          :disabled="disableDropdown || isDisable"
          popper-class="el-select-dropdown__depositDiscount"
        >
          <el-option v-for="(item, index) in voucherList" :key="item.code" :value="index">
            <div class="voucher_container">
              <div class="checkbox">
                <i v-if="selectedVoucherID === item.code" class="el-icon-success selected font_size"></i>
                <i v-else class="el-icon-unselected font_size"></i>
              </div>
              <div class="info">
                <p class="description font_size">
                  {{ $t('depositDiscount.description', { amount: item.amount }) }}
                </p>
                <div class="expireDate leading-none font_size flex flex-wrap items-start justify-start gap-[4px]">
                  <div class="flex items-stretch justify-start gap-[4px]">
                    <div>{{ $t('loyalty.myVouchers.voucherId') }}:&nbsp;{{ item.code }}</div>
                    <div class="w-px bg-[grey]"></div>
                  </div>
                  <div :class="{ expired: isExpiredSoon(item.expireTime) }">
                    {{ voucherExpireText(item.expireTime) }}
                  </div>
                </div>
              </div>
              <div class="tnc">
                <a
                  :href="REWARD_TNC[2]"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link"
                  class="hyperlink font_size"
                  >{{ $t('depositDiscount.tncApply') }}</a
                >
              </div>
            </div>
          </el-option>
          <!-- Display selected content -->
        </el-select>
        <div class="voucher_container w-full h-full relative pointer-events-none z-1">
          <div class="checkbox" v-if="selectedVoucherID">
            <i class="el-icon-success selected font_size"></i>
          </div>
          <div class="info" v-if="selectedVoucherID">
            <div class="description font_size leading-none flex flex-wrap items-start justify-start gap-[8px]">
              <div>
                {{
                  $t('depositDiscount.description', {
                    amount: selectedDiscountValue
                  })
                }}
              </div>
              <div class="flex items-stretch justify-start gap-[8px]">
                <div class="w-px bg-[grey]"></div>
                <div class="font_size gray leading-none normal-case font-normal">
                  {{ $t('loyalty.myVouchers.voucherId') }}:&nbsp;{{ selectedVoucherID }}
                </div>
              </div>
            </div>
            <div class="font_size gray leading-none">
              <div><span class="required_icon">*</span>{{ $t('depositDiscount.tnc') }}</div>
            </div>
          </div>
          <div
            class="selectVoucher font_size flex items-center justify-end whitespace-nowrap"
            :class="disableDropdown ? 'gray' : 'text-blueG0'"
          >
            {{ $t('depositDiscount.selectVoucher') }}
          </div>
        </div>
      </div>
    </div>
    <!-- Display Summary of payable and entered amount -->
    <div v-if="selectedVoucherID && enteredAmount" class="summary_container">
      <p class="title titleSummary font_size">{{ $t('depositDiscount.summary') }}</p>
      <ul class="clearfix">
        <li class="fl">
          <InputForm
            :label="$t('depositDiscount.youPay')"
            name="payableAmount"
            :disabled="true"
            :value="formatAmount(payableAmount)"
          ></InputForm>
          <p v-if="showConvertedAmount" class="conversion">
            <i class="el-icon-sort"></i> {{ formatConvertedAmount(convertedAmount) }}
            {{ this.discRateChange.currency }}
          </p>
        </li>
        <li class="fr">
          <InputForm
            :label="$t('depositDiscount.youReceive')"
            name="enteredAmount"
            :disabled="true"
            :value="formatAmount(enteredAmount)"
          ></InputForm>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import rounding from '@/util/rounding';
import { apiGetDepositDiscountVoucher } from '@/resource';
import { REWARD_TNC } from '@/constants/loyalty';
import moment from 'moment';
import { countryCodeEnum } from '@/constants/country';

export default {
  name: 'DepositDiscount',
  props: {
    depositAmount: {
      type: Number,
      default: 0
    },
    currency: {
      type: String,
      default: 'USD'
    },
    discRateChange: {
      type: Object,
      default: function () {
        return {};
      }
    },
    isDisable: { type: Boolean, default: false }
  },
  data() {
    return {
      voucherList: [],
      initialVoucherList: [],
      exchangeRateList: {},
      enteredAmount: 0,
      selectedVoucherID: 0,
      selectedDiscountAmount: 0,
      selectedEveryDepositAmount: 0,
      selectedMaxDiscountAmount: 0,
      selectedDiscountValue: 0,
      discountAmount: 0,
      REWARD_TNC,
      currencyWithoutDecimal: ['JPY']
    };
  },
  watch: {
    depositAmount(value) {
      this.setSelected();
      this.setDepositDiscount();
      this.voucherList = this.initialVoucherList.filter(voucher => this.rateConversion(voucher.deposit) <= value);

      return (this.enteredAmount = value.toFixed(2));
    }
  },
  computed: {
    disableDropdown() {
      return this.enteredAmount <= 0 || this.voucherList.length <= 0;
    },
    noVoucherAvailable() {
      return this.initialVoucherList.length <= 0;
    },
    showConvertedAmount() {
      return this.discRateChange.hasOwnProperty('currency');
    },
    listeners() {
      return Object.assign({}, this.$listeners, {
        input: index => {
          this.setSelected(
            this.selectedVoucherID === this.voucherList[index].code ? 0 : this.voucherList[index].code, // Voucher Code
            this.voucherList[index].amount, // Discount amount
            this.voucherList[index].deposit, // Every Deposit amount
            this.voucherList[index].amountCap // Maximum Discount amount
          );
          this.setDepositDiscount(this.payableAmount, this.selectedVoucherID);
        }
      });
    },
    payableAmount() {
      let factor = this.enteredAmount / this.selectedEveryDepositAmount;

      this.discountAmount = 0;

      this.discountAmount =
        factor < 1 && factor > 0 ? this.selectedDiscountAmount : Math.floor(factor) * this.selectedDiscountAmount;

      let capAmount = this.rateConversion(this.selectedMaxDiscountAmount);

      if (this.discountAmount > capAmount) this.discountAmount = capAmount;

      this.discountAmount = this.currency === 'JPY' ? rounding(Math.ceil, this.discountAmount, 0) : this.discountAmount;

      return rounding(Math.round, this.enteredAmount - this.discountAmount, 2);
    },
    convertedAmount() {
      switch (this.discRateChange.case) {
        case 1:
          return rounding(Math.ceil, this.payableAmount / this.discRateChange.rate, this.discRateChange.dp);
        case 2:
          return rounding(Math.round, this.discRateChange.rate * this.payableAmount, this.discRateChange.dp);
        case 3:
          return Math.ceil(this.discRateChange.rate * this.payableAmount).toFixed(this.discRateChange.dp);
        case 4:
          return (this.discRateChange.rate * this.payableAmount).toFixed(this.discRateChange.dp);
        case 5:
          let calculatedValue = 0;

          // Handling for China UnionPay
          if (this.discRateChange.paymentMethod === 'T00600') {
            let precision = Math.round(this.discRateChange.rate * this.payableAmount * 100) / 100;
            calculatedValue = Math.ceil(precision).toFixed(2);
          } else if (this.currency === 'JPY' || countryCodeEnum.CHILE === this.countryCode) {
            calculatedValue =
              this.discRateChange.rate * this.enteredAmount -
              rounding(Math.ceil, this.discRateChange.rate * this.discountAmount, 0);
          } else {
            calculatedValue = (Math.round(this.discRateChange.rate * this.payableAmount * 100) / 100).toFixed(2);
          }
          return calculatedValue;
        default:
          return rounding(Math.ceil, this.discRateChange.rate * this.payableAmount, this.discRateChange.dp);
      }
    },
    countryCode() {
      return parseInt(this.$store.state.common.countryCode);
    }
  },
  methods: {
    getDepositDiscountVoucher() {
      apiGetDepositDiscountVoucher()
        .then(resp => {
          if (resp.data.code === 0) {
            this.initialVoucherList = resp.data.data;
          }
        })
        .catch(err => {
          this.errorMessage(this.$t('responseMsg.500'));
        });
    },
    formatAmount(amount) {
      return this.currencyWithoutDecimal.includes(this.currency)
        ? this.$options.filters.numberWithCommas(parseFloat(amount))
        : this.$options.filters.formatNumber(parseFloat(amount, 2));
    },
    formatConvertedAmount(amount) {
      return this.discRateChange.currency === 'JPY'
        ? this.$options.filters.formatNumber(rounding(Math.floor, parseFloat(amount, 2), 0))
        : this.$options.filters.formatNumber(parseFloat(amount, 2));
    },
    isExpiredSoon(date) {
      let isExpireSoon = moment.utc(date).diff(moment(), 'days') < 1;
      return isExpireSoon;
    },
    voucherExpireText(date) {
      let newDate = `${moment(date).format('DD')} ${this.$t(
        `common.month.${moment(date).format('MMM').toLowerCase()}`
      )} ${moment(date).format('YYYY')}`;
      let renderText = this.isExpiredSoon(date)
        ? this.$t('loyalty.redeemVoucher.expiresIn', {
            duration:
              moment.utc(date).diff(moment(), 'hours') > 0
                ? `${this.$t('common.hour', { hour: moment.utc(date).diff(moment(), 'hours') })}`
                : `${this.$t('common.minute', { minutes: moment.utc(date).diff(moment(), 'minutes') })}`
          })
        : this.$t('loyalty.redeemVoucher.validTill', {
            date: newDate
          });
      return renderText;
    },
    setSelected(voucherid = 0, discount = 0, deposit = 0, amountCap = 0) {
      this.selectedVoucherID = voucherid;
      this.selectedDiscountAmount = this.rateConversion(discount, false);
      this.selectedEveryDepositAmount = this.rateConversion(deposit);
      this.selectedMaxDiscountAmount = amountCap;
      this.selectedDiscountValue = discount;
    },
    setDepositDiscount(payableAmount = 0, voucherID = 0) {
      this.$emit('setDepositDiscount', {
        payableAmount: payableAmount,
        voucherID: voucherID
      });
    },
    rateConversion(amount, round = true) {
      switch (this.currency) {
        case 'JPY':
          return round ? rounding(Math.ceil, amount * this.discRateChange.rate, 0) : amount * this.discRateChange.rate;
        case 'VND':
          return amount * this.discRateChange.rate;
        case 'USC':
          return (amount * 1000000) / 10000;
        default:
          return amount;
      }
    }
  },
  mounted() {
    this.getDepositDiscountVoucher();
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/components/depositDiscount.scss';
</style>
